import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Building2, Plus, Pencil, Trash2, Check, Search, X, Users,
  ShieldCheck, ChevronUp, ChevronDown, ChevronsUpDown, Settings2,
  ChevronLeft, ChevronRight, Loader2, LayoutGrid, FileText, CreditCard, Settings,
  Clock, Sparkles, CalendarDays, Mail, Phone, Briefcase, UserCheck,
} from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TC500 = "#6366f1";
const TC50 = "#eef2ff";

const BASE_TABS = [
  { id: "workspace", label: "Workspace", icon: LayoutGrid },
  { id: "empresa", label: "Empresa", icon: Building2 },
  { id: "assinatura", label: "Assinatura", icon: CreditCard },
  { id: "configuracoes", label: "Configurações", icon: Settings },
];

function ClientDetailModal({ tenant, open, onClose, tab, setTab, onUpdated }) {
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [freeAccess, setFreeAccess] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [clientPricing, setClientPricing] = useState(null);
  const [pricingCustom, setPricingCustom] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    if (tenant) {
      setEditName(tenant.name || "");
      setTrialLoading(true);
      axios.get(`${API}/tenants/${tenant.tenant_id}/trial`, { withCredentials: true })
        .then(res => { setTrialInfo(res.data); setFreeAccess(!!res.data?.free_access); })
        .catch(() => setTrialInfo(null))
        .finally(() => setTrialLoading(false));
      setTxLoading(true);
      axios.get(`${API}/stripe/transactions/${tenant.tenant_id}`, { withCredentials: true })
        .then(res => setTransactions(res.data || []))
        .catch(() => setTransactions([]))
        .finally(() => setTxLoading(false));
      axios.get(`${API}/owner/tenant-pricing/${tenant.tenant_id}`, { withCredentials: true })
        .then(res => { setClientPricing({ base_price: res.data.base_price, extra_user_price: res.data.extra_user_price, included_users: res.data.included_users }); setPricingCustom(res.data.custom); })
        .catch(() => setClientPricing(null));
    }
  }, [tenant]);

  const tabs = useMemo(() => {
    return [...BASE_TABS];
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };

  const trialDaysLeft = useMemo(() => {
    if (!trialInfo?.trial_end) return null;
    try {
      const end = new Date(trialInfo.trial_end.replace("Z", "+00:00"));
      const days = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
      return days;
    } catch { return null; }
  }, [trialInfo]);

  const saveName = async () => {
    if (!tenant || !editName.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/tenants/${tenant.tenant_id}`, { name: editName }, { withCredentials: true });
      toast.success("Nome atualizado!");
      onUpdated?.();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl !p-0 overflow-hidden bg-white sm:!p-0">
        <div className="flex h-[560px]">
          {/* Sidebar */}
          <div className="w-48 bg-slate-50 border-r border-slate-200 p-3 flex-shrink-0 flex flex-col">
            <div className="flex items-center gap-2.5 px-2 mb-4 pt-1">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ backgroundColor: TC500 }}>
                {tenant.name?.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{tenant.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{tenant.tenant_id}</p>
              </div>
            </div>

            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Menu</p>
            <div className="space-y-0.5">
              {tabs.map(t => (
                <button key={t.id}
                  data-testid={`client-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${tab === t.id ? "text-white shadow-sm font-medium" : "text-slate-500 hover:bg-white/60 hover:text-slate-700"}`}
                  style={tab === t.id ? { backgroundColor: TC500 } : undefined}>
                  <t.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  {t.label}
                  {t.id === "trial" && (
                    <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${trialDaysLeft > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>
                      {trialDaysLeft > 0 ? `${trialDaysLeft}d` : "Exp"}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {tab === "workspace" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Workspace</h3>
                  <p className="text-xs text-slate-500 mt-1">Informações do workspace deste cliente.</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-700 text-sm">Nome do Workspace</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nome do workspace" />
                      <Button size="sm" onClick={saveName} disabled={saving} style={{ backgroundColor: TC500 }} className="text-white hover:opacity-90 flex-shrink-0">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 text-sm">ID</Label>
                      <p className="text-sm text-slate-500 font-mono mt-1 bg-slate-50 rounded px-3 py-2">{tenant.tenant_id}</p>
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm">Criado em</Label>
                      <p className="text-sm text-slate-500 mt-1 bg-slate-50 rounded px-3 py-2">
                        {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString("pt-BR") : "-"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 text-sm">Admin</Label>
                      <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-violet-500" />{tenant.admin_name || "-"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm">Email Admin</Label>
                      <p className="text-sm text-slate-500 mt-1">{tenant.admin_email || "-"}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">Usuários</Label>
                    <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-slate-400" />{tenant.user_count || 0} usuário(s)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {tab === "trial" && trialInfo?.has_trial && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Trial</h3>
                  <p className="text-xs text-slate-500 mt-1">Informações do período de teste deste cliente.</p>
                </div>

                {/* Status badge */}
                <div className="flex items-center gap-3">
                  <Badge className={`text-xs px-3 py-1 border-0 ${trialDaysLeft > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>
                    {trialDaysLeft > 0 ? `Ativo — ${trialDaysLeft} dias restantes` : "Expirado"}
                  </Badge>
                  <span className="text-xs text-slate-400">Status: <span className="font-medium text-slate-600">{trialInfo.status || "-"}</span></span>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Início</span>
                    </div>
                    <p className="text-sm font-medium text-slate-700">{formatDate(trialInfo.trial_start)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Expiração</span>
                    </div>
                    <p className="text-sm font-medium text-slate-700">{formatDate(trialInfo.trial_end)}</p>
                  </div>
                </div>

                {/* Lead info */}
                <div>
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Dados do Lead</p>
                  <div className="space-y-2.5">
                    {trialInfo.cargo && (
                      <div className="flex items-center gap-2.5">
                        <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Cargo</p>
                          <p className="text-sm text-slate-700">{trialInfo.cargo}</p>
                        </div>
                      </div>
                    )}
                    {trialInfo.setor && (
                      <div className="flex items-center gap-2.5">
                        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Setor</p>
                          <p className="text-sm text-slate-700">{trialInfo.setor}</p>
                        </div>
                      </div>
                    )}
                    {trialInfo.tamanho_empresa && (
                      <div className="flex items-center gap-2.5">
                        <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Tamanho da Empresa</p>
                          <p className="text-sm text-slate-700">{trialInfo.tamanho_empresa}</p>
                        </div>
                      </div>
                    )}
                    {trialInfo.crm_atual && (
                      <div className="flex items-center gap-2.5">
                        <UserCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">CRM Atual</p>
                          <p className="text-sm text-slate-700">{trialInfo.crm_atual}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "empresa" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Empresa</h3>
                  <p className="text-xs text-slate-500 mt-1">Visão geral do cliente, status e uso.</p>
                </div>

                {/* Overall Status */}
                {(() => {
                  const subStatus = tenant.subscription_status;
                  const isActive = subStatus === "active";
                  const isCancelling = subStatus === "cancelling";
                  const hasTrial = trialInfo?.has_trial;
                  const trialExpired = hasTrial && trialDaysLeft !== null && trialDaysLeft <= 0;
                  const inTrial = hasTrial && trialDaysLeft > 0 && !isActive;

                  let statusLabel, statusColor, statusBg, statusBorder, statusIcon;
                  if (isActive) {
                    statusLabel = "Pagando"; statusColor = "text-emerald-700"; statusBg = "bg-emerald-50"; statusBorder = "border-emerald-200"; statusIcon = <Check className="w-5 h-5 text-emerald-600" />;
                  } else if (isCancelling) {
                    statusLabel = "Cancelamento agendado"; statusColor = "text-amber-700"; statusBg = "bg-amber-50"; statusBorder = "border-amber-200"; statusIcon = <Clock className="w-5 h-5 text-amber-600" />;
                  } else if (inTrial) {
                    statusLabel = "Em Trial"; statusColor = "text-blue-700"; statusBg = "bg-blue-50"; statusBorder = "border-blue-200"; statusIcon = <Sparkles className="w-5 h-5 text-blue-600" />;
                  } else if (trialExpired) {
                    statusLabel = "Trial Expirado"; statusColor = "text-rose-700"; statusBg = "bg-rose-50"; statusBorder = "border-rose-200"; statusIcon = <Clock className="w-5 h-5 text-rose-500" />;
                  } else {
                    statusLabel = "Sem plano"; statusColor = "text-slate-600"; statusBg = "bg-slate-50"; statusBorder = "border-slate-200"; statusIcon = <Building2 className="w-5 h-5 text-slate-400" />;
                  }

                  const plan = tenant.subscription_plan;
                  const contractedUsers = plan?.total_users || (hasTrial ? 3 : 0);
                  const registeredUsers = tenant.user_count || 0;
                  const usagePercent = contractedUsers > 0 ? Math.min(100, Math.round((registeredUsers / contractedUsers) * 100)) : 0;

                  return (
                    <>
                      {/* Status banner */}
                      <div className={`${statusBg} border ${statusBorder} rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${statusBg} border ${statusBorder} flex items-center justify-center`}>
                            {statusIcon}
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-bold ${statusColor}`}>{statusLabel}</p>
                            {isActive && plan && (
                              <p className="text-xs text-slate-500 mt-0.5">Plano ativo — R${plan.amount || 0}/mês — {plan.total_users || 1} usuário(s)</p>
                            )}
                            {isCancelling && tenant.subscription_period_end && (
                              <p className="text-xs text-amber-600 mt-0.5">Acesso até {formatDate(tenant.subscription_period_end)}</p>
                            )}
                            {inTrial && (
                              <p className="text-xs text-blue-600 mt-0.5">{trialDaysLeft} dias restantes — Expira em {formatDate(trialInfo.trial_end)}</p>
                            )}
                            {trialExpired && (
                              <p className="text-xs text-rose-500 mt-0.5"></p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Info grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" />Workspace</p>
                          <p className="text-sm font-medium text-slate-800">{tenant.name}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" />Criado em</p>
                          <p className="text-sm font-medium text-slate-800">{formatDate(tenant.created_at)}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Admin</p>
                          <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                            {tenant.admin_name || "-"}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3" />{tenant.admin_email || "-"}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><CreditCard className="w-3 h-3" />Assinatura</p>
                          {isActive ? (
                            <>
                              <p className="text-sm font-medium text-emerald-700">R${plan?.amount || 0}/mês</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">Ativada em {tenant.subscription_activated_at ? new Date(tenant.subscription_activated_at).toLocaleDateString("pt-BR") : "-"}</p>
                            </>
                          ) : (
                            <p className="text-sm font-medium text-slate-500">-</p>
                          )}
                        </div>
                      </div>

                      {/* User usage bar */}
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <p className="text-sm font-semibold text-slate-700">Usuários</p>
                          </div>
                          <p className="text-sm font-bold text-slate-800">
                            {registeredUsers} <span className="text-slate-400 font-normal">/ {contractedUsers}</span>
                          </p>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${usagePercent >= 100 ? "bg-rose-500" : usagePercent >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${usagePercent}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1.5">
                          <p className="text-[10px] text-slate-400">Cadastrados: {registeredUsers}</p>
                          <p className="text-[10px] text-slate-400">Contratados: {contractedUsers}</p>
                        </div>
                      </div>

                      {/* Lead data from trial */}
                      {trialInfo?.has_trial && (trialInfo.cargo || trialInfo.setor || trialInfo.tamanho_empresa || trialInfo.crm_atual) && (
                        <div>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Dados do Lead</p>
                          <div className="grid grid-cols-2 gap-3">
                            {trialInfo.cargo && (
                              <div className="flex items-center gap-2.5">
                                <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Cargo</p>
                                  <p className="text-sm text-slate-700">{trialInfo.cargo}</p>
                                </div>
                              </div>
                            )}
                            {trialInfo.setor && (
                              <div className="flex items-center gap-2.5">
                                <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Setor</p>
                                  <p className="text-sm text-slate-700">{trialInfo.setor}</p>
                                </div>
                              </div>
                            )}
                            {trialInfo.tamanho_empresa && (
                              <div className="flex items-center gap-2.5">
                                <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Tamanho</p>
                                  <p className="text-sm text-slate-700">{trialInfo.tamanho_empresa}</p>
                                </div>
                              </div>
                            )}
                            {trialInfo.crm_atual && (
                              <div className="flex items-center gap-2.5">
                                <UserCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">CRM Atual</p>
                                  <p className="text-sm text-slate-700">{trialInfo.crm_atual}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {tab === "assinatura" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Assinatura</h3>
                  <p className="text-xs text-slate-500 mt-1">Status da assinatura e histórico de pagamentos.</p>
                </div>

                {/* Free access toggle */}
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${freeAccess ? "bg-emerald-100" : "bg-slate-100"}`}>
                      <CreditCard className={`w-4 h-4 ${freeAccess ? "text-emerald-600" : "text-slate-400"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Acesso gratuito</p>
                      <p className="text-[11px] text-slate-400">Ao ativar, este tenant usa o sistema sem restrição de trial ou cobrança.</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !freeAccess;
                      try {
                        await axios.post(`${API}/owner/tenant-free-access/${tenant.tenant_id}`, { free_access: newVal }, { withCredentials: true });
                        setFreeAccess(newVal);
                        toast.success(newVal ? "Acesso gratuito ativado!" : "Acesso gratuito desativado.");
                      } catch { toast.error("Erro"); }
                    }}
                    className={`w-10 h-6 rounded-full flex items-center transition-colors px-0.5 flex-shrink-0 ${freeAccess ? "justify-end bg-emerald-500" : "justify-start bg-slate-300"}`}>
                    <div className="w-5 h-5 bg-white rounded-full shadow" />
                  </button>
                </div>

                {/* Status card + Pricing — hidden when free access */}
                {!freeAccess && (
                <>
                {/* Status card */}
                {(() => {
                  const subStatus = tenant.subscription_status;
                  const plan = tenant.subscription_plan;
                  const hasTrial = trialInfo?.has_trial;
                  const isActive = subStatus === "active";
                  const isTrial = hasTrial && !isActive;
                  const isExpired = !isActive && trialInfo?.status === "expired";
                  const isCancelled = trialInfo?.status === "cancelled";

                  const statusConfig = isActive
                    ? { bg: "bg-emerald-50", border: "border-emerald-200", icon: <Check className="w-4 h-4 text-emerald-600" />, iconBg: "bg-emerald-100", label: "Assinatura ativa", labelColor: "text-emerald-800", badgeBg: "bg-emerald-100 text-emerald-700" }
                    : isTrial
                    ? { bg: "bg-blue-50", border: "border-blue-200", icon: <Clock className="w-4 h-4 text-blue-600" />, iconBg: "bg-blue-100", label: "Período de teste", labelColor: "text-blue-800", badgeBg: "bg-blue-100 text-blue-700" }
                    : isExpired
                    ? { bg: "bg-amber-50", border: "border-amber-200", icon: <Clock className="w-4 h-4 text-amber-600" />, iconBg: "bg-amber-100", label: "Trial expirado", labelColor: "text-amber-800", badgeBg: "bg-amber-100 text-amber-700" }
                    : isCancelled
                    ? { bg: "bg-slate-50", border: "border-slate-200", icon: <X className="w-4 h-4 text-slate-500" />, iconBg: "bg-slate-100", label: "Cancelada", labelColor: "text-slate-700", badgeBg: "bg-slate-200 text-slate-600" }
                    : { bg: "bg-slate-50", border: "border-slate-200", icon: <CreditCard className="w-4 h-4 text-slate-400" />, iconBg: "bg-slate-100", label: "Sem assinatura", labelColor: "text-slate-600", badgeBg: "bg-slate-200 text-slate-600" };

                  return (
                    <div className={`${statusConfig.bg} border ${statusConfig.border} rounded-lg p-4`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full ${statusConfig.iconBg} flex items-center justify-center`}>
                            {statusConfig.icon}
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${statusConfig.labelColor}`}>{statusConfig.label}</p>
                            {isActive && plan && (
                              <p className="text-xs text-emerald-600">
                                {plan.total_users || 1} usuário(s) — R${plan.amount || 49}/mês
                              </p>
                            )}
                            {isTrial && trialDaysLeft !== null && (
                              <p className="text-xs text-blue-600">
                                {trialDaysLeft > 0 ? `${trialDaysLeft} dias restantes` : "Expirado"}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={`text-[10px] border-0 ${statusConfig.badgeBg}`}>
                          {isActive ? "Ativa" : isTrial ? "Trial" : isExpired ? "Expirado" : isCancelled ? "Cancelada" : "—"}
                        </Badge>
                      </div>

                    </div>
                  );
                })()}

                {/* Per-client pricing */}
                {clientPricing && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      Precificação deste cliente
                      {pricingCustom && (
                        <Badge className="text-[10px] text-white border-0" style={{ backgroundColor: TC500 }}>Valor personalizado</Badge>
                      )}
                    </p>
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-[10px] text-slate-400">Base (R$/mês)</Label>
                          <Input type="number" step="0.01" min="0" className="h-8 text-sm" value={clientPricing.base_price} onChange={e => setClientPricing(p => ({ ...p, base_price: parseFloat(e.target.value) || 0 }))} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Inclusos</Label>
                          <Input type="number" min="1" className="h-8 text-sm" value={clientPricing.included_users} onChange={e => setClientPricing(p => ({ ...p, included_users: parseInt(e.target.value) || 1 }))} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Adicional (R$)</Label>
                          <Input type="number" step="0.01" min="0" className="h-8 text-sm" value={clientPricing.extra_user_price} onChange={e => setClientPricing(p => ({ ...p, extra_user_price: parseFloat(e.target.value) || 0 }))} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" disabled={savingPricing} onClick={async () => {
                          setSavingPricing(true);
                          try {
                            await axios.put(`${API}/owner/tenant-pricing/${tenant.tenant_id}`, clientPricing, { withCredentials: true });
                            setPricingCustom(true);
                            toast.success("Precificação salva!");
                          } catch { toast.error("Erro ao salvar"); }
                          finally { setSavingPricing(false); }
                        }}>Salvar</Button>
                        {pricingCustom && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                            try {
                              await axios.delete(`${API}/owner/tenant-pricing/${tenant.tenant_id}`, { withCredentials: true });
                              const def = await axios.get(`${API}/owner/pricing`, { withCredentials: true });
                              setClientPricing({ base_price: def.data.base_price, extra_user_price: def.data.extra_user_price, included_users: def.data.included_users });
                              setPricingCustom(false);
                              toast.success("Voltou ao valor padrão");
                            } catch { toast.error("Erro"); }
                          }}>Resetar para padrão</Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction history — compact */}
                <div>
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Histórico de transações</p>
                  {txLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    </div>
                  ) : transactions.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">Nenhuma transação registrada.</p>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="text-left px-3 py-1.5 font-semibold text-slate-400 uppercase">Data</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-slate-400 uppercase">Valor</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-slate-400 uppercase">Usuários</th>
                            <th className="text-right px-3 py-1.5 font-semibold text-slate-400 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx, i) => (
                            <tr key={tx.session_id || i} className="border-b border-slate-100 last:border-0">
                              <td className="px-3 py-1.5 text-slate-600">{tx.created_at ? new Date(tx.created_at).toLocaleDateString("pt-BR") : "-"}</td>
                              <td className="px-3 py-1.5 text-slate-800 font-medium">R${tx.amount?.toFixed(2)}</td>
                              <td className="px-3 py-1.5 text-slate-600">{tx.total_users || 1}</td>
                              <td className="px-3 py-1.5 text-right">
                                <Badge className={`text-[9px] border-0 ${tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : tx.payment_status === "initiated" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                                  {tx.payment_status === "paid" ? "Pago" : tx.payment_status === "initiated" ? "Pendente" : tx.payment_status || "—"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                </>
                )}
              </div>
            )}

            {tab === "configuracoes" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Configurações</h3>
                  <p className="text-xs text-slate-500 mt-1">Configurações específicas deste cliente.</p>
                </div>
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Settings className="w-10 h-10 mb-3 text-slate-300" />
                  <p className="text-sm">Em breve. Configurações avançadas do cliente.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantsPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ tenant_name: "", admin_name: "", admin_email: "" });
  const [error, setError] = useState("");

  // Selection
  const [rowSelection, setRowSelection] = useState({});

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState({});

  // Sort
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);
  useEffect(() => { setRowSelection({}); }, [tenants]);

  // Client-side filter + sort
  const filtered = useMemo(() => {
    let data = tenants;
    if (searchDebounced) {
      const s = searchDebounced.toLowerCase();
      data = data.filter(tn =>
        tn.name?.toLowerCase().includes(s) ||
        tn.admin_email?.toLowerCase().includes(s) ||
        tn.admin_name?.toLowerCase().includes(s) ||
        tn.tenant_id?.toLowerCase().includes(s)
      );
    }
    // Sort
    data = [...data].sort((a, b) => {
      const av = (a[sortBy] || "").toString().toLowerCase();
      const bv = (b[sortBy] || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [tenants, searchDebounced, sortBy, sortDir]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  const formatDate = (iso) => {
    if (!iso) return "\u2014";
    try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
  };

  // Columns
  const columns = useMemo(() => [
    {
      id: "select",
      size: 40,
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          data-testid="select-all-tenants"
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          data-testid={`select-tenant-${row.original.tenant_id}`}
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Nome",
      size: 240,
      cell: ({ row }) => {
        const tn = row.original;
        return (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ backgroundColor: TC500 }}>
              {tn.name?.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium text-slate-900 truncate">{tn.name}</span>
          </div>
        );
      },
    },
    {
      id: "admin_name",
      accessorKey: "admin_name",
      header: "Admin",
      size: 180,
      cell: ({ row }) => {
        const tn = row.original;
        if (!tn.admin_name) return <span className="text-slate-300">-</span>;
        return (
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
            <span className="text-sm text-slate-600 truncate">{tn.admin_name}</span>
          </div>
        );
      },
    },
    {
      id: "admin_email",
      accessorKey: "admin_email",
      header: "Email Admin",
      size: 220,
      cell: ({ row }) => {
        const email = row.original.admin_email;
        if (!email) return <span className="text-slate-300">-</span>;
        return <span className="text-sm text-slate-600 truncate">{email}</span>;
      },
    },
    {
      id: "user_count",
      accessorKey: "user_count",
      header: "Usuários",
      size: 100,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-sm text-slate-600">{row.original.user_count || 0}</span>
        </div>
      ),
    },
    {
      id: "status",
      accessorKey: "subscription_status",
      header: "Status",
      size: 130,
      cell: ({ row }) => {
        const tn = row.original;
        const st = tn.subscription_status;
        if (st === "active") return <Badge className="text-[10px] border-0 bg-emerald-100 text-emerald-700">Pagando</Badge>;
        if (st === "cancelling") return <Badge className="text-[10px] border-0 bg-amber-100 text-amber-700">Cancelando</Badge>;
        return <Badge className="text-[10px] border-0 bg-slate-100 text-slate-500">Trial</Badge>;
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Criado em",
      size: 120,
      cell: ({ getValue }) => <span className="text-sm text-slate-500">{formatDate(getValue())}</span>,
    },
    {
      id: "actions",
      size: 80,
      enableSorting: false,
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-0.5 justify-end">
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid={`edit-tenant-${row.original.tenant_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600"
              onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid={`delete-tenant-${row.original.tenant_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500"
              onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger><TooltipContent>Remover</TooltipContent></Tooltip>
        </div>
      ),
    },
  ], [t]); // eslint-disable-line

  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    getRowId: (row) => row.tenant_id,
  });

  const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
  const hasSelection = selectedIds.length > 0;

  const toggleableColumns = useMemo(() => {
    return columns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({
      id: c.id,
      label: c.header || c.id,
    }));
  }, [columns]);

  // CRUD
  const openNew = () => { setEditTenant(null); setForm({ tenant_name: "", admin_name: "", admin_email: "" }); setError(""); setDialogOpen(true); };

  // Detail modal
  const [detailTenant, setDetailTenant] = useState(null);
  const [detailTab, setDetailTab] = useState("workspace");

  const openDetail = (tn) => { setDetailTenant(tn); setDetailTab("workspace"); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      await axios.post(`${API}/tenants`, form, { withCredentials: true });
      toast.success("Cliente criado!");
      setDialogOpen(false); fetchTenants();
    } catch (err) { setError(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/tenants/${deleteTarget.tenant_id}`, { withCredentials: true });
      toast.success("Cliente removido!");
      fetchTenants();
    } catch (err) { console.error(err); }
    setDeleteTarget(null);
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API}/tenants/${id}`, { withCredentials: true })));
      toast.success(`${selectedIds.length} cliente(s) removido(s)`);
      setRowSelection({});
      fetchTenants();
    } catch { toast.error("Erro ao excluir"); }
  };

  return (
    <div data-testid="tenants-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: TC50 }}>
            <Building2 className="w-5 h-5" style={{ color: TC500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              Clientes
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filtered.length}</Badge>
              {hasSelection && (
                <Tooltip><TooltipTrigger asChild>
                  <Badge data-testid="tenant-bulk-count" className="text-white text-sm font-medium px-3 py-1 cursor-default" style={{ backgroundColor: TC500 }}>{selectedIds.length}</Badge>
                </TooltipTrigger><TooltipContent>{selectedIds.length} cliente(s) selecionado(s)</TooltipContent></Tooltip>
              )}
            </h1>
            <p className="text-slate-500 mt-0.5">Gerencie os clientes/workspaces do sistema.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSelection && (
            <>
              <Tooltip><TooltipTrigger asChild>
                <Button data-testid="tenant-bulk-delete" size="icon" className="h-9 w-9 text-white bg-rose-500 hover:bg-rose-600" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger><TooltipContent>Excluir selecionados</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setRowSelection({})}>
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger><TooltipContent>Limpar seleção</TooltipContent></Tooltip>
              <div className="w-px h-6 bg-slate-200" />
            </>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="tenant-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="tenant-col-visibility" variant="outline" size="icon" className="h-9 w-9 text-slate-600">
                <Settings2 className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visíveis</p>
              {toggleableColumns.map(col => {
                const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true;
                return (
                  <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                    <Checkbox checked={isVisible} onCheckedChange={(v) => table.getColumn(col.id)?.toggleVisibility(!!v)} />
                    {col.label}
                  </label>
                );
              })}
            </PopoverContent>
          </Popover>
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid="add-tenant-btn" onClick={openNew} size="icon" style={{ backgroundColor: TC500 }} className="text-white h-9 w-9 hover:opacity-90">
              <Plus className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Novo Cliente</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => {
                    const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey;
                    return (
                      <th key={header.id}
                        style={{ width: header.getSize() }}
                        className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""}`}
                        onClick={() => canSort && handleSort(header.column.columnDef.accessorKey)}>
                        <div className="flex items-center gap-1.5">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon col={header.column.columnDef.accessorKey} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {columns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (
                      <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-16">
                    <div className="flex flex-col items-center text-slate-400">
                      <Building2 className="w-10 h-10 mb-3 text-slate-300" />
                      <p className="text-sm">{tenants.length === 0 ? "Nenhum cliente cadastrado" : "Nenhum resultado encontrado"}</p>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.map(row => (
                <tr key={row.id}
                  data-testid={`tenant-row-${row.original.tenant_id}`}
                  className={`border-b border-slate-100 transition-colors cursor-pointer ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`}
                  onClick={() => openDetail(row.original)}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">
            {filtered.length > 0 ? `${filtered.length} cliente(s)` : "Nenhum resultado"}
          </p>
        </div>
      </div>

      {/* Create New Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: TC500 }} />
              Novo Cliente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label className="text-slate-700">Nome do workspace</Label>
                <Input value={form.tenant_name} onChange={e => setForm({ ...form, tenant_name: e.target.value })} required placeholder="Ex: Empresa XYZ" />
              </div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t("ui.admin_tenant")}</p>
              <div>
                <Label className="text-slate-700">Nome completo</Label>
                <Input value={form.admin_name} onChange={e => setForm({ ...form, admin_name: e.target.value })} required placeholder="Ex: João Silva" />
              </div>
              <div>
                <Label className="text-slate-700">E-mail</Label>
                <Input type="email" value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} required placeholder="joao@empresa.com" />
                <p className="text-xs text-slate-400 mt-1">O admin recebera um convite neste e-mail.</p>
              </div>
            </div>
            {error && <p className="text-xs text-rose-500 bg-rose-50 rounded px-3 py-2">{error}</p>}
            <Button type="submit" style={{ backgroundColor: TC500 }} className="w-full text-white hover:opacity-90">
              <Check className="w-4 h-4 mr-2" />Criar Cliente
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Detail Modal */}
      <ClientDetailModal
        tenant={detailTenant}
        open={!!detailTenant}
        onClose={() => setDetailTenant(null)}
        tab={detailTab}
        setTab={setDetailTab}
        onUpdated={fetchTenants}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Remover cliente"
        description={`Tem certeza que deseja remover o cliente "${deleteTarget?.name}"? Todos os usuários deste cliente perderão acesso.`}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
